/**
 * Onboarding Service
 * Manages onboarding state and progress for carrier, broker, and shipper users.
 * Stores progress in companyUsers.onboardingProgress nested object.
 * Step lists are role-specific: carrier has 9 steps; broker and shipper have 6 (no equipment, driver_profile, vvid).
 */

import { getFailoverAwareDatabase } from '../config/firebaseWithFailover';
import { doc, getDoc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { CompanyUser } from '../types/user';
import { MigrationDataAccessLayer } from './migrationDataAccessLayer';
import Logger from './logger';

export interface OnboardingProgress {
  currentStep: string;
  completedSteps: string[];
  stepData: {
    [stepName: string]: any;
  };
  progressPercentage: number;
  lastSavedAt?: Date;
}

export interface OnboardingServiceResult {
  success: boolean;
  error?: string;
  data?: any;
}

/** User type for onboarding; determines which step list is used. */
export type OnboardingUserType = 'carrier' | 'broker' | 'shipper';

/** Single step definition (id, display name, order). */
export interface OnboardingStepDef {
  id: string;
  name: string;
  order: number;
}

// --- Phase 1: Step config (single source of truth) ---

/** Carrier: full 9-step flow (welcome, basic_info, equipment, service_areas, documents, driver_profile, vvid, payment, review). */
const CARRIER_ONBOARDING_STEPS: OnboardingStepDef[] = [
  { id: 'welcome', name: 'Welcome', order: 0 },
  { id: 'basic_info', name: 'Basic Information', order: 1 },
  { id: 'equipment', name: 'Equipment', order: 2 },
  { id: 'service_areas', name: 'Service Areas', order: 3 },
  { id: 'documents', name: 'Documents', order: 4 },
  { id: 'driver_profile', name: 'Driver Profile', order: 5 },
  { id: 'vvid', name: 'VVID', order: 6 },
  { id: 'payment', name: 'Payment Settings', order: 7 },
  { id: 'review', name: 'Review', order: 8 },
];

/** Broker & Shipper: 6-step flow (no equipment, driver_profile, vvid). */
const BROKER_SHIPPER_ONBOARDING_STEPS: OnboardingStepDef[] = [
  { id: 'welcome', name: 'Welcome', order: 0 },
  { id: 'basic_info', name: 'Basic Information', order: 1 },
  { id: 'service_areas', name: 'Service Areas', order: 2 },
  { id: 'documents', name: 'Documents', order: 3 },
  { id: 'payment', name: 'Payment Settings', order: 4 },
  { id: 'review', name: 'Review', order: 5 },
];

/**
 * Returns the ordered step list for the given user type.
 * Carrier: 9 steps; broker/shipper: 6 steps.
 */
export function getOnboardingSteps(userType: OnboardingUserType): OnboardingStepDef[] {
  switch (userType) {
    case 'carrier':
      return CARRIER_ONBOARDING_STEPS;
    case 'broker':
    case 'shipper':
      return BROKER_SHIPPER_ONBOARDING_STEPS;
    default:
      return CARRIER_ONBOARDING_STEPS;
  }
}

/** Carrier steps; backward-compatible export for existing carrier onboarding UI. */
export const ONBOARDING_STEPS = CARRIER_ONBOARDING_STEPS;

export const onboardingService = {
  /**
   * Check if user has completed onboarding
   */
  async checkOnboardingStatus(userId: string): Promise<boolean> {
    try {
      const db = getFailoverAwareDatabase();
      const userRef = doc(db, 'companyUsers', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return false;
      }
      
      const userData = userDoc.data();
      return userData.onboardingCompleted === true;
    } catch (error: any) {
      Logger.error('Error checking onboarding status:', error as Error);
      return false;
    }
  },

  /**
   * Get onboarding progress for a user
   */
  async getOnboardingProgress(userId: string): Promise<OnboardingProgress | null> {
    try {
      const db = getFailoverAwareDatabase();
      const userRef = doc(db, 'companyUsers', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return null;
      }
      
      const userData = userDoc.data();
      const progress = userData.onboardingProgress;
      
      if (!progress) {
        // Initialize default progress
        return {
          currentStep: 'welcome',
          completedSteps: [],
          stepData: {},
          progressPercentage: 0,
        };
      }
      
      // Convert Firestore Timestamp to Date if needed
      return {
        currentStep: progress.currentStep || 'welcome',
        completedSteps: progress.completedSteps || [],
        stepData: progress.stepData || {},
        progressPercentage: progress.progressPercentage || 0,
        lastSavedAt: progress.lastSavedAt?.toDate ? progress.lastSavedAt.toDate() : progress.lastSavedAt,
      };
    } catch (error: any) {
      Logger.error('Error getting onboarding progress:', error as Error);
      return null;
    }
  },


  /**
   * Update onboarding progress for a specific step
   * Saves data to TOP-LEVEL FIELDS (same as My Profile) + tracks progress separately
   */
  async updateOnboardingProgress(
    userId: string,
    step: string,
    data: any,
    markCompleted: boolean = false,
    userType: OnboardingUserType = 'carrier'
  ): Promise<OnboardingServiceResult> {
    try {
      const db = getFailoverAwareDatabase();
      const userRef = doc(db, 'companyUsers', userId);
      const migrationDataAccess = new MigrationDataAccessLayer();
      
      // Get current progress (for tracking only)
      const currentProgress = await this.getOnboardingProgress(userId) || {
        currentStep: 'welcome',
        completedSteps: [],
        stepData: {},
        progressPercentage: 0,
      };
      
      // NOTE: Data saving to top-level fields is handled by saveProgress() in OnboardingWalkthrough.tsx
      // This function only tracks progress (stepData, completedSteps, etc.)
      // The actual profile data is saved via the merged flattened data in saveProgress()
      
      // Helper function to recursively remove undefined values (Firestore doesn't allow undefined)
      const removeUndefined = (obj: any): any => {
        if (obj === null || obj === undefined) {
          return null; // Convert undefined to null for Firestore
        }
        if (Array.isArray(obj)) {
          return obj.map(removeUndefined);
        }
        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
              cleaned[key] = removeUndefined(value);
            }
          }
          return cleaned;
        }
        return obj;
      };
      
      // Clean the data before saving (remove undefined values)
      const cleanedData = removeUndefined(data);
      
      // Update progress tracking (separate from actual data)
      // Don't save review step data - it's not a form step
      const updatedStepData = {
        ...currentProgress.stepData,
      };
      
      // Only save step data if it's not the review step
      if (step !== 'review') {
        updatedStepData[step] = cleanedData; // Keep step data for review step display
      }
      
      // Clean the entire stepData object to remove any undefined values
      const cleanedStepData = removeUndefined(updatedStepData);
      
      // Resolve role step list first (for filtering and progress)
      const steps = getOnboardingSteps(userType);
      const roleStepIds = new Set(steps.map((s) => s.id));

      // Update completed steps if marking as completed
      let updatedCompletedSteps = [...currentProgress.completedSteps];
      if (markCompleted && !updatedCompletedSteps.includes(step) && step !== 'review') {
        updatedCompletedSteps.push(step);
      }
      // Keep only steps that exist in this role's flow (prevents broker persisting equipment/driver/vvid)
      updatedCompletedSteps = updatedCompletedSteps.filter((id) => roleStepIds.has(id));

      // Calculate progress percentage (use role's step count; exclude welcome; cap at 100%)
      const totalSteps = steps.length - 1;
      const progressPercentage = Math.min(100, Math.round((updatedCompletedSteps.length / totalSteps) * 100));
      
      // Build update object conditionally to avoid undefined values
      const updateData: any = {
        'onboardingProgress.currentStep': step,
        'onboardingProgress.completedSteps': updatedCompletedSteps,
        'onboardingProgress.stepData': cleanedStepData, // Keep for review step display (cleaned of undefined)
        'onboardingProgress.progressPercentage': progressPercentage,
        'onboardingProgress.lastSavedAt': serverTimestamp(),
      };
      
      // Only set onboardingStartedAt on first save (when lastSavedAt doesn't exist)
      if (!currentProgress.lastSavedAt) {
        updateData['onboardingStartedAt'] = serverTimestamp();
      }
      // If it already exists, don't include it in the update (Firestore will keep existing value)
      
      // Update progress tracking only (not the actual data - that's already saved above)
      await updateDoc(userRef, updateData);
      
      return {
        success: true,
        data: {
          currentStep: step,
          completedSteps: updatedCompletedSteps,
          stepData: updatedStepData,
          progressPercentage,
        },
      };
    } catch (error: any) {
      Logger.error('Error updating onboarding progress:', error as Error);
      return {
        success: false,
        error: error.message || 'Failed to update onboarding progress',
      };
    }
  },

  /**
   * Save step data without marking as completed
   */
  async saveStepData(userId: string, step: string, data: any): Promise<OnboardingServiceResult> {
    return this.updateOnboardingProgress(userId, step, data, false);
  },

  /**
   * Mark a step as completed
   */
  async completeStep(userId: string, step: string, data: any): Promise<OnboardingServiceResult> {
    return this.updateOnboardingProgress(userId, step, data, true);
  },

  /**
   * Calculate progress percentage based on completed steps for the given user type.
   */
  async calculateProgress(userId: string, userType: OnboardingUserType = 'carrier'): Promise<number> {
    try {
      const progress = await this.getOnboardingProgress(userId);
      if (!progress) {
        return 0;
      }
      const steps = getOnboardingSteps(userType);
      const totalSteps = steps.length - 1; // Exclude welcome step
      const roleStepIds = new Set(steps.map((s) => s.id));
      const completedInRole = progress.completedSteps.filter((id) => roleStepIds.has(id));
      return Math.min(100, Math.round((completedInRole.length / totalSteps) * 100));
    } catch (error: any) {
      Logger.error('Error calculating progress:', error as Error);
      return 0;
    }
  },

  /**
   * Complete onboarding (set onboardingCompleted = true)
   */
  async completeOnboarding(userId: string): Promise<OnboardingServiceResult> {
    try {
      const db = getFailoverAwareDatabase();
      const userRef = doc(db, 'companyUsers', userId);
      
      await updateDoc(userRef, {
        onboardingCompleted: true,
        onboardingCompletedAt: serverTimestamp(),
      });
      
      return {
        success: true,
        data: { onboardingCompleted: true },
      };
    } catch (error: any) {
      Logger.error('Error completing onboarding:', error as Error);
      return {
        success: false,
        error: error.message || 'Failed to complete onboarding',
      };
    }
  },

  /**
   * Get next step in the onboarding flow for the given user type.
   */
  getNextStep(currentStep: string, userType: OnboardingUserType = 'carrier'): string | null {
    const steps = getOnboardingSteps(userType);
    const currentIndex = steps.findIndex(step => step.id === currentStep);
    if (currentIndex === -1 || currentIndex === steps.length - 1) {
      return null;
    }
    return steps[currentIndex + 1].id;
  },

  /**
   * Get previous step in the onboarding flow for the given user type.
   */
  getPreviousStep(currentStep: string, userType: OnboardingUserType = 'carrier'): string | null {
    const steps = getOnboardingSteps(userType);
    const currentIndex = steps.findIndex(step => step.id === currentStep);
    if (currentIndex <= 0) {
      return null;
    }
    return steps[currentIndex - 1].id;
  },

  /**
   * Get step definition by ID for the given user type.
   */
  getStepById(stepId: string, userType: OnboardingUserType = 'carrier') {
    const steps = getOnboardingSteps(userType);
    return steps.find(step => step.id === stepId) ?? null;
  },
};

export default onboardingService;

